// Area pages. Each entry is deliberately written differently — different
// opening, structure, local references and phrasing — so no two pages read
// alike. Facts are kept general (local geography and housing character only);
// nothing about specific jobs, clients or figures is invented.

export const AREA_PAGES = [
  {
    id: "molesey",
    path: "/builders-molesey",
    name: "Molesey",
    metaTerm: "Builders in Molesey",
    title: "Builders in Molesey | DMS Home Improvements",
    metaDescription:
      "Family-run builders and plasterers in Molesey, Surrey. Extensions, loft conversions, kitchens, bathrooms and plastering, with a free site visit and a fixed written quote.",
    h1: "Builders in Molesey",
    intro:
      "Molesey is our home patch. We are based here, we know the streets, and most weeks you will find one of our vans somewhere between the river and the main road. If you are planning building work in Molesey, you are dealing with a local team, not a call centre.",
    sections: [
      {
        heading: "A builder that is actually local to Molesey",
        paragraphs: [
          "Being based in Molesey means we can pop round for a look without it being a half-day expedition. That matters more than people think. It keeps the free site visit genuinely free, it means deliveries and skips are easy to arrange, and it means that if you have a question halfway through the job we are minutes away rather than the far side of the county.",
          "The housing here is a real mix, from the older cottages and period terraces near the church to the 1930s semis and the newer closes. Each one wants a slightly different approach, and after 20+ years working in and around the area we have seen most of what a Molesey house can throw at us.",
        ],
      },
      {
        heading: "The work we take on here",
        paragraphs: [
          "Rear and side extensions are the biggest thing we do in Molesey, usually to open up a tired kitchen into a proper family space. Loft conversions are close behind, as families decide to build up rather than move on. Alongside that we fit kitchens and bathrooms, re-plaster whole houses and turn out for the smaller jobs plenty of firms will not, from a cracked ceiling to a patch after a leak.",
          "Whatever the size, the routine is the same: we come and look properly, we explain what we would do and why, and we put a fixed written price in your hand before anyone lifts a tool.",
        ],
      },
    ],
    neighbours: ["East Molesey", "West Molesey", "Thames Ditton", "Kingston upon Thames"],
    photos: [
      { id: "builders-molesey-recent-project.webp", note: "PHOTO NEEDED: a completed project in Molesey, landscape, min 1600px wide", alt: "Completed building project in Molesey by DMS Home Improvements" },
      { id: "builders-molesey-team-on-site.webp", note: "PHOTO NEEDED: the team working on a Molesey job, landscape, min 1600px wide", alt: "DMS Home Improvements team on a building job in Molesey" },
    ],
    faqs: [
      {
        q: "Are you really based in Molesey?",
        a: "Yes. Molesey is where we are based and where a lot of our work is, which is why we can offer a genuinely free, no-pressure site visit here without it costing us half a day.",
      },
      {
        q: "Do you cover both East and West Molesey?",
        a: "We do. We work right across Molesey and out into the surrounding towns, so wherever you are in the area, call Dave and we will arrange a look.",
      },
    ],
  },
  {
    id: "east-molesey",
    path: "/builders-east-molesey",
    name: "East Molesey",
    metaTerm: "Builders in East Molesey",
    title: "Builders in East Molesey | DMS Home Improvements",
    metaDescription:
      "Local builders and plasterers in East Molesey, KT8. Extensions, lofts, kitchens, bathrooms, refurbishments and plastering. Free site visit, fixed written quote.",
    h1: "Builders in East Molesey",
    intro:
      "Tucked between Hampton Court and the river, East Molesey has some of the loveliest period homes in the area — and period homes need a builder who understands them. That is exactly the kind of work we enjoy most.",
    sections: [
      {
        heading: "Working on East Molesey's older homes",
        paragraphs: [
          "A lot of East Molesey sits within conservation areas and close to Hampton Court, so sympathetic work matters here. Original features, older brickwork and lime plaster all need handling with a bit of respect rather than being ripped straight out. We are comfortable matching new work to old, whether that is blending an extension into an existing roofline or bringing a tired room back to a proper flat finish.",
          "Older houses also spring surprises once you open them up. We would rather find those on a careful site visit and price for them honestly than paper over them and hit you with extras later.",
        ],
      },
      {
        heading: "From full extensions to a single ceiling",
        paragraphs: [
          "In East Molesey we build extensions, convert lofts, fit kitchens and bathrooms and take on full refurbishments where a house is being brought up to date top to bottom. We also do a steady stream of plastering, from re-skimming a whole floor of a house to repairing a cracked or sagging ceiling in one of the older properties.",
          "Because we are only next door in Molesey, getting to you is never a problem, and neither is calling back if you need us once the job is finished.",
        ],
      },
    ],
    neighbours: ["Molesey", "Thames Ditton", "Kingston upon Thames", "Surbiton"],
    photos: [
      { id: "builders-east-molesey-period-home.webp", note: "PHOTO NEEDED: work on a period home in East Molesey, landscape, min 1600px wide", alt: "Period home building work in East Molesey by DMS Home Improvements" },
      { id: "builders-east-molesey-finished-room.webp", note: "PHOTO NEEDED: a finished room in East Molesey, landscape, min 1600px wide", alt: "Finished renovated room in East Molesey by DMS Home Improvements" },
    ],
    faqs: [
      {
        q: "Do you work on houses in conservation areas?",
        a: "Yes. Plenty of East Molesey falls within conservation areas, and we are used to keeping work sympathetic to the age and character of the property. Where planning or consent is needed, we will talk you through it honestly.",
      },
      {
        q: "Can you match new plaster and brickwork to an older house?",
        a: "We do this regularly. Matching finishes so a repair or extension does not stand out is a big part of working on older East Molesey homes, and it is something we take pride in getting right.",
      },
    ],
  },
  {
    id: "west-molesey",
    path: "/builders-west-molesey",
    name: "West Molesey",
    metaTerm: "Builders in West Molesey",
    title: "Builders in West Molesey | DMS Home Improvements",
    metaDescription:
      "Reliable builders and plasterers in West Molesey. Extensions, loft conversions, kitchen and bathroom fitting, refurbishments and plastering. Free quotes, tidy work.",
    h1: "Builders in West Molesey",
    intro:
      "West Molesey is full of family homes with room to grow — the sort of houses where a well-planned extension or a converted loft can add a whole new stage of life without the upheaval of moving. That is where we come in.",
    sections: [
      {
        heading: "Space for a growing family",
        paragraphs: [
          "Much of West Molesey is made up of post-war semis and detached houses with decent-sized plots. That gives you options: a single-storey rear extension for a bigger kitchen and dining space, a two-storey extension to add a bedroom and bathroom above, or a loft conversion that turns dead roof space into a room you will actually use.",
          "We help you work out which of those makes sense for your house and your budget, and we are straight with you if one option would give you far better value than another.",
        ],
      },
      {
        heading: "One team for the whole job",
        paragraphs: [
          "The advantage of using us in West Molesey is that the same family-run team handles the build, the plastering and the finishing trades, rather than you having to chase half a dozen separate contractors. We fit the kitchen or bathroom, skim the walls and hand back a room that is ready to decorate.",
          "We are just up the road, so site visits, deliveries and the odd snagging call are all quick and easy to sort.",
        ],
      },
    ],
    neighbours: ["East Molesey", "Molesey", "Walton-on-Thames", "Esher"],
    photos: [
      { id: "builders-west-molesey-extension.webp", note: "PHOTO NEEDED: a completed extension in West Molesey, landscape, min 1600px wide", alt: "Completed house extension in West Molesey by DMS Home Improvements" },
      { id: "builders-west-molesey-kitchen.webp", note: "PHOTO NEEDED: a finished kitchen in West Molesey, landscape, min 1600px wide", alt: "Finished fitted kitchen in West Molesey by DMS Home Improvements" },
    ],
    faqs: [
      {
        q: "Would an extension or a loft conversion suit my West Molesey house better?",
        a: "It depends on the plot, the layout and what you need the extra space for. On the free site visit we will look at both options and tell you honestly which one gives you more usable space for your money.",
      },
      {
        q: "Do I need to use separate trades for the building and the finishing?",
        a: "No. We handle the build, the plastering and the fit-out with our own team, so you have one point of contact for the whole job rather than juggling several firms.",
      },
    ],
  },
  {
    id: "kingston-upon-thames",
    path: "/builders-kingston-upon-thames",
    name: "Kingston upon Thames",
    metaTerm: "Builders in Kingston upon Thames",
    title: "Builders in Kingston upon Thames | DMS Home Improvements",
    metaDescription:
      "Builders and plasterers in Kingston upon Thames. Extensions, loft conversions, kitchens, bathrooms and full refurbishments. Free site visit and fixed written quote.",
    h1: "Builders in Kingston upon Thames",
    intro:
      "Kingston is a busy place to build. Terraced streets, tight access, parking to think about and neighbours close by — none of it fazes us. We have been working across Kingston and its surrounding districts for over two decades.",
    sections: [
      {
        heading: "Building where space is tight",
        paragraphs: [
          "A lot of Kingston housing is Victorian and Edwardian terraces, particularly around the town centre, Norbiton and towards the river. These make wonderful homes but they demand care: shared party walls, limited side access, and materials that need bringing in and out without turning the street into a building site.",
          "We plan the logistics before we start — where the skip goes, how materials arrive, how we keep the place tidy — so the job runs smoothly for you and stays on good terms with the neighbours.",
        ],
      },
      {
        heading: "Extensions, lofts and full refurbishments",
        paragraphs: [
          "The classic Kingston project is a side-return or rear extension that finally gives a narrow terrace a proper kitchen-diner, often paired with a loft conversion to add a bedroom up top. We also carry out whole-house refurbishments for people buying an older Kingston property that needs dragging into the present day.",
          "Every one of those jobs includes our own plastering, so the finish is consistent from the first room to the last.",
        ],
      },
    ],
    neighbours: ["Surbiton", "East Molesey", "Thames Ditton", "New Malden"],
    photos: [
      { id: "builders-kingston-terrace-extension.webp", note: "PHOTO NEEDED: side-return or rear extension on a Kingston terrace, landscape, min 1600px wide", alt: "Terrace extension in Kingston upon Thames by DMS Home Improvements" },
      { id: "builders-kingston-refurbishment.webp", note: "PHOTO NEEDED: a refurbished Kingston property, landscape, min 1600px wide", alt: "Refurbished period property in Kingston upon Thames by DMS Home Improvements" },
    ],
    faqs: [
      {
        q: "Can you build an extension on a terraced house with narrow access?",
        a: "Yes, this is common in Kingston. We plan how materials and waste come in and out before we start so the job runs cleanly, and we keep the site tidy and considerate to neighbours throughout.",
      },
      {
        q: "Do you take on full renovations of older Kingston houses?",
        a: "We do. Whole-house refurbishments of Victorian and Edwardian properties are a big part of our work, from first fix through to plastering and final finishes.",
      },
    ],
  },
  {
    id: "surbiton",
    path: "/builders-surbiton",
    name: "Surbiton",
    metaTerm: "Builders in Surbiton",
    title: "Builders in Surbiton | DMS Home Improvements",
    metaDescription:
      "Trusted builders and plasterers in Surbiton. Extensions, loft conversions, kitchens, bathrooms, refurbishments and plastering. Free estimates, fixed written quotes.",
    h1: "Builders in Surbiton",
    intro:
      "Surbiton rewards good building work. Its handsome Victorian villas and mansion flats have generous proportions, and homeowners here tend to want work done properly and finished to a high standard. That suits us perfectly.",
    sections: [
      {
        heading: "Quality that matches the houses",
        paragraphs: [
          "The character of Surbiton — high ceilings, bay windows, original detailing — means the standard of finish really shows. A rushed skim or a poorly matched cornice sticks out a mile in a room like this. We work to the level these houses deserve, whether we are re-plastering a whole floor or fitting a new kitchen.",
          "That care extends to the parts you do not see, too: proper preparation, sound materials and doing things once, properly, rather than twice.",
        ],
      },
      {
        heading: "What Surbiton homeowners ask us for",
        paragraphs: [
          "Loft conversions are hugely popular in Surbiton, turning the roof space of a tall villa into a light-filled main bedroom or study. Rear extensions and kitchen refurbishments come a close second. We also handle bathrooms, from a smart en-suite in a converted loft to a full family bathroom refit.",
          "As with everywhere we work, it starts with a free site visit and a fixed written quote so you know exactly where you stand.",
        ],
      },
    ],
    neighbours: ["Kingston upon Thames", "Thames Ditton", "Long Ditton", "Tolworth"],
    photos: [
      { id: "builders-surbiton-loft.webp", note: "PHOTO NEEDED: a finished loft conversion in Surbiton, landscape, min 1600px wide", alt: "Loft conversion in a Surbiton villa by DMS Home Improvements" },
      { id: "builders-surbiton-plastering.webp", note: "PHOTO NEEDED: freshly plastered period room in Surbiton, landscape, min 1600px wide", alt: "Freshly plastered period room in Surbiton by DMS Home Improvements" },
    ],
    faqs: [
      {
        q: "Can you convert the loft of a tall Victorian villa?",
        a: "Yes. The generous roof space in many Surbiton villas is ideal for a conversion, and we handle everything from the structural work to the plastering and finishing so it ends up as a proper room.",
      },
      {
        q: "Do you match original features like cornicing?",
        a: "We do our best to keep new work in step with the character of the house, including repairing or blending in coving and cornice where a ceiling has been re-done.",
      },
    ],
  },
  {
    id: "thames-ditton",
    path: "/builders-thames-ditton",
    name: "Thames Ditton",
    metaTerm: "Builders in Thames Ditton",
    title: "Builders in Thames Ditton | DMS Home Improvements",
    metaDescription:
      "Builders and plasterers in Thames Ditton. Extensions, loft conversions, kitchen and bathroom fitting, refurbishments and plastering. Free site visit, fixed price.",
    h1: "Builders in Thames Ditton",
    intro:
      "Thames Ditton is one of those villages that people move to and never leave — which is exactly why so many homeowners here choose to improve and extend rather than sell up. We are just across the way in Molesey, so we are a natural fit for building work in the village.",
    sections: [
      {
        heading: "Improve, don't move",
        paragraphs: [
          "With a village this sought-after, the sums usually point towards making more of the home you already have. A rear extension for a bigger kitchen, a loft conversion for another bedroom, or a full refurbishment of a period property can transform how a house works without the cost and stress of moving on.",
          "We help you get the most out of the footprint and character you have, and we are honest about where your money is best spent.",
        ],
      },
      {
        heading: "A short hop from our base",
        paragraphs: [
          "Thames Ditton sits right next to Molesey, so we know the area well and getting to you takes minutes. That makes the free site visit easy and keeps the whole job responsive — if something needs a quick look, we are not far away.",
          "From the first measure to the final coat of plaster, the same team looks after your project, and everything is set out in a fixed written quote before we start.",
        ],
      },
    ],
    neighbours: ["Long Ditton", "Surbiton", "East Molesey", "Esher"],
    photos: [
      { id: "builders-thames-ditton-extension.webp", note: "PHOTO NEEDED: a completed extension in Thames Ditton, landscape, min 1600px wide", alt: "Completed rear extension in Thames Ditton by DMS Home Improvements" },
      { id: "builders-thames-ditton-interior.webp", note: "PHOTO NEEDED: a finished interior in Thames Ditton, landscape, min 1600px wide", alt: "Finished renovated interior in Thames Ditton by DMS Home Improvements" },
    ],
    faqs: [
      {
        q: "Is it worth extending rather than moving in Thames Ditton?",
        a: "For many homeowners here it makes real sense, given how sought-after the village is. On the free site visit we will give you a straight view on what extending or converting would add and what it would involve.",
      },
      {
        q: "How quickly can you come and quote?",
        a: "Thames Ditton is right next to our base in Molesey, so arranging a free site visit is usually quick and easy. Call Dave and we will find a time that suits you.",
      },
    ],
  },
  {
    id: "esher",
    path: "/builders-esher",
    name: "Esher",
    metaTerm: "Builders in Esher",
    title: "Builders in Esher | DMS Home Improvements",
    metaDescription:
      "Builders and plasterers in Esher, Surrey. Extensions, loft conversions, kitchens, bathrooms and high-quality refurbishments. Free site visit and fixed written quote.",
    h1: "Builders in Esher",
    intro:
      "Esher homes are often larger, set back from the road and finished to a high standard — the kind of properties where the quality of the building work really has to stand up to scrutiny. We are set up for exactly that.",
    sections: [
      {
        heading: "Bigger projects, higher finish",
        paragraphs: [
          "Around Esher we see more substantial extensions, garage and outbuilding conversions, and full refurbishments of larger family homes. These projects have more moving parts, so clear communication and careful project management count for a lot. You will always know what is happening this week and what is coming next.",
          "The finish has to match the house, and we hold ourselves to that: neat joinery lines, flat walls, crisp plastering and a site left clean at the end of each day.",
        ],
      },
      {
        heading: "One family-run team you can rely on",
        paragraphs: [
          "For a bigger Esher project you want a builder who turns up, keeps their word and sees it through — not a firm that vanishes when a bigger job comes along. As a family-run business we live and die by our reputation locally, so looking after you properly is simply how we work.",
          "Everything begins with a free site visit and a fixed written quote, and our own plastering ties the whole job together.",
        ],
      },
    ],
    neighbours: ["Thames Ditton", "Claygate", "Cobham", "Hersham"],
    photos: [
      { id: "builders-esher-large-extension.webp", note: "PHOTO NEEDED: a larger extension or refurbishment in Esher, landscape, min 1600px wide", alt: "Large house extension in Esher by DMS Home Improvements" },
      { id: "builders-esher-finished-project.webp", note: "PHOTO NEEDED: a finished high-spec project in Esher, landscape, min 1600px wide", alt: "Finished high-specification project in Esher by DMS Home Improvements" },
    ],
    faqs: [
      {
        q: "Do you take on larger, higher-specification projects?",
        a: "Yes. Much of our Esher work is at the larger end — bigger extensions and full refurbishments — where finish and project management matter. We are happy to talk through how we would run a project of that size.",
      },
      {
        q: "Will the same team see my project through?",
        a: "As a family-run firm, the same core team looks after your job from start to finish. Our local reputation depends on it, so we do not disappear part way through.",
      },
    ],
  },
  {
    id: "cobham",
    path: "/builders-cobham",
    name: "Cobham",
    metaTerm: "Builders in Cobham",
    title: "Builders in Cobham | DMS Home Improvements",
    metaDescription:
      "Builders and plasterers covering Cobham, Surrey. Extensions, loft conversions, kitchens, bathrooms and full refurbishments. Free site visit, fixed written quote.",
    h1: "Builders in Cobham",
    intro:
      "Cobham and the surrounding villages have some of the most individual homes in Surrey, from period cottages to substantial detached houses. Building work here is rarely off-the-shelf, and we like it that way.",
    sections: [
      {
        heading: "Work tailored to the house",
        paragraphs: [
          "No two Cobham projects are the same. One week it is a careful extension to a character property, the next a full refurbishment of a large family home or a loft conversion to add another suite. We take the time to understand what you actually want from the space before we start pricing, rather than reaching for a standard template.",
          "That tailored approach is easier when the same people who quote the job are the ones who build it — which, as a family-run firm, is exactly how we work.",
        ],
      },
      {
        heading: "Trades under one roof",
        paragraphs: [
          "For homeowners around Cobham, one of the biggest advantages of using us is that the building work, the plastering and the fit-out are all handled by our own team. That keeps the standard consistent and saves you from coordinating a string of separate contractors.",
          "Cobham is towards the edge of our patch, so we would always start with a free site visit to make sure a project is right for us and right for you before anything is agreed.",
        ],
      },
    ],
    neighbours: ["Esher", "Oxshott", "Stoke D'Abernon", "Downside"],
    photos: [
      { id: "builders-cobham-character-home.webp", note: "PHOTO NEEDED: work on a character home in Cobham, landscape, min 1600px wide", alt: "Building work on a character home in Cobham by DMS Home Improvements" },
      { id: "builders-cobham-finished-space.webp", note: "PHOTO NEEDED: a finished space in Cobham, landscape, min 1600px wide", alt: "Finished renovated space in Cobham by DMS Home Improvements" },
    ],
    faqs: [
      {
        q: "Do you cover Cobham and the nearby villages?",
        a: "We do. Cobham is towards the edge of the area we cover, so we always begin with a free site visit to make sure the project is a good fit before anything is committed.",
      },
      {
        q: "Can you handle an individual, one-off project?",
        a: "Absolutely. Much of the work around Cobham is bespoke rather than standard, and we tailor each project to the house and to what you want from the space.",
      },
    ],
  },
];

export const getAreaBySlug = (path) => AREA_PAGES.find((a) => a.path === path);
